import { useState, useEffect, useCallback, useMemo } from 'react';
import useAppStore from '../../store/app.store';
import PageHeader from '../../components/ui/PageHeader';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import { format, parseISO, isValid } from 'date-fns';

const API = 'http://localhost:3001/api';
const moeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = d => { try { const dt = typeof d === 'string' ? parseISO(d) : d; return isValid(dt) ? format(dt, 'dd/MM/yyyy') : '—'; } catch { return '—'; } };

const TOKEN = () => localStorage.getItem('auth_token') || '';
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${TOKEN()}` });

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, { ...opts, headers: { ...authHeaders(), ...(opts.headers || {}) } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
  return data;
}

// ─── STATUS MAP ───────────────────────────────────────────────────────────────

const STATUS_COR = {
  PENDING:    { cor: '#B45309', bg: '#FEF3C7', label: 'Pendente' },
  RECEIVED:   { cor: '#065F46', bg: '#D1FAE5', label: 'Recebido' },
  CONFIRMED:  { cor: '#065F46', bg: '#D1FAE5', label: 'Confirmado' },
  OVERDUE:    { cor: '#991B1B', bg: '#FEE2E2', label: 'Vencido'  },
  REFUNDED:   { cor: '#1E40AF', bg: '#DBEAFE', label: 'Estornado' },
  CHARGEBACK_REQUESTED: { cor: '#6B21A8', bg: '#F3E8FF', label: 'Chargeback' },
  AWAITING_RISK_ANALYSIS: { cor: '#92400E', bg: '#FEF3C7', label: 'Em análise' },
  pago:      { cor: '#065F46', bg: '#D1FAE5', label: 'Pago' },
  pendente:  { cor: '#B45309', bg: '#FEF3C7', label: 'Pendente' },
  vencido:   { cor: '#991B1B', bg: '#FEE2E2', label: 'Vencido' },
  cancelado: { cor: '#374151', bg: '#F3F4F6', label: 'Cancelado' },
  estornado: { cor: '#1E40AF', bg: '#DBEAFE', label: 'Estornado' },
};
const Chip = ({ s }) => {
  const c = STATUS_COR[s] || { cor: '#374151', bg: '#F3F4F6', label: s };
  return <span style={{ color: c.cor, background: c.bg, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{c.label}</span>;
};

const BILLING_LABEL = { PIX: 'PIX', BOLETO: 'Boleto', CREDIT_CARD: 'Cartão', UNDEFINED: 'Qualquer' };

// ═══════════════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function Financeiro() {
  const clientes = useAppStore(s => s.clientes);
  const [aba, setAba] = useState('recorrentes');

  const abas = [
    { id: 'recorrentes',   label: 'Recorrentes' },
    { id: 'dividas',       label: 'Dívidas' },
    { id: 'transacoes',    label: 'Transações' },
    { id: 'cobrancas',     label: 'Cobranças Asaas' },
    { id: 'extrato',       label: 'Extrato' },
    { id: 'transferencias', label: 'Transferências' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0B1220' }}>
      <div style={{ padding: '24px 28px' }}>
        <PageHeader title="Financeiro" subtitle="Gestão financeira integrada com Asaas" />

        {/* Abas */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #2A3652', marginBottom: 24 }}>
          {abas.map(a => (
            <button key={a.id} onClick={() => setAba(a.id)} style={{
              padding: '10px 18px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
              background: 'transparent', borderBottom: aba === a.id ? '2px solid #6366F1' : '2px solid transparent',
              color: aba === a.id ? '#A5B4FC' : '#64748B', transition: 'all .15s',
            }}>{a.label}</button>
          ))}
        </div>

        {aba === 'recorrentes'    && <AbaRecorrentes />}
        {aba === 'dividas'        && <AbaDividas />}
        {aba === 'transacoes'     && <AbaTransacoes clientes={clientes} />}
        {aba === 'cobrancas'      && <AbaCobrancas  clientes={clientes} />}
        {aba === 'extrato'        && <AbaExtrato />}
        {aba === 'transferencias' && <AbaTransferencias />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ABA TRANSAÇÕES (BD LOCAL)
// ═══════════════════════════════════════════════════════════════════════════════

function AbaTransacoes({ clientes }) {
  const [transacoes, setTransacoes] = useState([]);
  const [resumo, setResumo] = useState({});
  const [filtros, setFiltros] = useState({ tipo: '', status: '', data_inicio: '', data_fim: '' });
  const [loading, setLoading] = useState(true);
  const [modalNovo, setModalNovo] = useState(false);
  const [baixando, setBaixando] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)));
      const [lista, res] = await Promise.all([
        apiFetch(`/financeiro/transacoes?${params}`),
        apiFetch('/financeiro/resumo'),
      ]);
      setTransacoes(lista.data || []);
      setResumo(res);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvarTransacao = async (form) => {
    try {
      await apiFetch('/financeiro/transacoes', { method: 'POST', body: JSON.stringify(form) });
      toast.success('Transação criada!');
      setModalNovo(false);
      carregar();
    } catch (e) { toast.error(e.message); }
  };

  const darBaixa = async (id) => {
    setBaixando(id);
    try {
      await apiFetch(`/financeiro/transacoes/${id}/baixar`, {
        method: 'PATCH',
        body: JSON.stringify({ data_pagamento: new Date().toISOString().split('T')[0] }),
      });
      toast.success('Baixa registrada!');
      carregar();
    } catch (e) { toast.error(e.message); }
    finally { setBaixando(null); }
  };

  const card = (label, value, cor) => (
    <div key={label} style={{ background: '#131D31', borderRadius: 12, padding: '16px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: cor, marginTop: 6 }}>{moeda(value)}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {card('Receitas mês',  resumo.receitas_mes, '#34D399')}
        {card('Despesas mês',  resumo.despesas_mes, '#F87171')}
        {card('Saldo mês',     resumo.saldo_mes, (resumo.saldo_mes || 0) >= 0 ? '#34D399' : '#F87171')}
        {card('A receber',     resumo.a_receber, '#60A5FA')}
        {card('Vencidos',      resumo.vencidos, '#FB923C')}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {['tipo', 'status', 'data_inicio', 'data_fim'].map(k => k.startsWith('data') ? (
          <input key={k} type="date" value={filtros[k]} onChange={e => setFiltros(p => ({ ...p, [k]: e.target.value }))}
            style={inputSt} />
        ) : (
          <select key={k} value={filtros[k]} onChange={e => setFiltros(p => ({ ...p, [k]: e.target.value }))} style={inputSt}>
            {k === 'tipo' ? <>
              <option value="">Todos os tipos</option><option value="receita">Receita</option><option value="despesa">Despesa</option>
            </> : <>
              <option value="">Todos status</option><option value="pendente">Pendente</option><option value="pago">Pago</option>
              <option value="vencido">Vencido</option><option value="cancelado">Cancelado</option>
            </>}
          </select>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={() => setModalNovo(true)} style={btnPrimary}>+ Nova Transação</button>
      </div>

      <Tabela loading={loading} colunas={['Descrição', 'Cliente', 'Tipo', 'Valor', 'Vencimento', 'Status', '']}>
        {transacoes.map(t => {
          const vencido = t.status === 'pendente' && new Date(t.data_vencimento) < new Date();
          return (
            <tr key={t.id} style={{ background: vencido ? 'rgba(239,68,68,0.05)' : 'transparent' }}>
              <td style={tdSt}><span style={{ color: '#E2E8F0', fontWeight: 500 }}>{t.descricao}</span></td>
              <td style={tdSt}><span style={{ color: '#64748B', fontSize: 12 }}>{t.cliente_nome || '—'}</span></td>
              <td style={tdSt}><Chip s={t.tipo === 'receita' ? 'pago' : 'cancelado'} /></td>
              <td style={{ ...tdSt, fontWeight: 700, color: t.tipo === 'receita' ? '#34D399' : '#F87171' }}>{moeda(t.valor)}</td>
              <td style={{ ...tdSt, color: vencido ? '#F87171' : '#94A3B8', fontSize: 12 }}>{fmtData(t.data_vencimento)}{vencido && ' ⚠'}</td>
              <td style={tdSt}><Chip s={vencido ? 'vencido' : t.status} /></td>
              <td style={tdSt}>
                {t.status === 'pendente' && (
                  <button onClick={() => darBaixa(t.id)} disabled={baixando === t.id}
                    style={{ color: '#818CF8', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    {baixando === t.id ? 'Salvando...' : 'Dar baixa'}
                  </button>
                )}
                {t.asaas_payment_id && (
                  <span style={{ color: '#64748B', fontSize: 11, marginLeft: 6 }}>Asaas ✓</span>
                )}
              </td>
            </tr>
          );
        })}
        {!loading && !transacoes.length && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#475569', padding: 40, fontSize: 13 }}>Nenhuma transação encontrada.</td></tr>}
      </Tabela>

      <Modal open={modalNovo} onClose={() => setModalNovo(false)} title="Nova Transação">
        <FormNovaTransacao clientes={clientes.filter(c => c.status === 'ativo')}
          onSalvar={salvarTransacao} onFechar={() => setModalNovo(false)} />
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ABA COBRANÇAS ASAAS
// ═══════════════════════════════════════════════════════════════════════════════

function AbaCobrancas({ clientes }) {
  const [cobrancas, setCobrancas] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ status: '', dueDate_ge: '', dueDate_le: '' });
  const [modalNova, setModalNova] = useState(false);
  const [modalPix, setModalPix] = useState(null);
  const [modalBoleto, setModalBoleto] = useState(null);
  const [loadingAcao, setLoadingAcao] = useState({});

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filtros).filter(([, v]) => v)));
      const [lista, bal] = await Promise.all([
        apiFetch(`/financeiro/asaas/cobrancas?${params}`),
        apiFetch('/financeiro/asaas/saldo').catch(() => null),
      ]);
      setCobrancas(lista.data || []);
      setSaldo(bal);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const verPix = async (id) => {
    setLoadingAcao(p => ({ ...p, [id]: 'pix' }));
    try {
      const dados = await apiFetch(`/financeiro/asaas/cobrancas/${id}/pix`);
      setModalPix(dados);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingAcao(p => ({ ...p, [id]: null })); }
  };

  const verBoleto = async (id) => {
    setLoadingAcao(p => ({ ...p, [id]: 'boleto' }));
    try {
      const dados = await apiFetch(`/financeiro/asaas/cobrancas/${id}/boleto`);
      setModalBoleto(dados);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingAcao(p => ({ ...p, [id]: null })); }
  };

  const cancelar = async (id) => {
    if (!window.confirm('Cancelar esta cobrança?')) return;
    setLoadingAcao(p => ({ ...p, [id]: 'cancel' }));
    try {
      await apiFetch(`/financeiro/asaas/cobrancas/${id}`, { method: 'DELETE' });
      toast.success('Cobrança cancelada.');
      carregar();
    } catch (e) { toast.error(e.message); }
    finally { setLoadingAcao(p => ({ ...p, [id]: null })); }
  };

  const criarCobranca = async (form) => {
    try {
      await apiFetch('/financeiro/asaas/cobrancas', { method: 'POST', body: JSON.stringify(form) });
      toast.success('Cobrança criada no Asaas!');
      setModalNova(false);
      carregar();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      {/* Saldo */}
      {saldo && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Saldo disponível', value: saldo.balance, cor: '#34D399' },
          ].map(c => (
            <div key={c.label} style={{ background: '#131D31', borderRadius: 12, padding: '16px 20px' }}>
              <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>{c.label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: c.cor, marginTop: 4 }}>{moeda(c.value)}</div>
            </div>
          ))}
          <div style={{ flex: 1 }} />
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filtros.status} onChange={e => setFiltros(p => ({ ...p, status: e.target.value }))} style={inputSt}>
          <option value="">Todos status</option>
          <option value="PENDING">Pendente</option>
          <option value="RECEIVED">Recebido</option>
          <option value="OVERDUE">Vencido</option>
          <option value="REFUNDED">Estornado</option>
        </select>
        <input type="date" value={filtros.dueDate_ge} onChange={e => setFiltros(p => ({ ...p, dueDate_ge: e.target.value }))} style={inputSt} placeholder="Venc. de" />
        <input type="date" value={filtros.dueDate_le} onChange={e => setFiltros(p => ({ ...p, dueDate_le: e.target.value }))} style={inputSt} placeholder="Venc. até" />
        <div style={{ flex: 1 }} />
        <button onClick={() => setModalNova(true)} style={btnPrimary}>+ Nova Cobrança Asaas</button>
      </div>

      <Tabela loading={loading} colunas={['Cliente Asaas', 'Descrição', 'Tipo', 'Valor', 'Vencimento', 'Status', 'Ações']}>
        {cobrancas.map(c => (
          <tr key={c.id}>
            <td style={tdSt}><span style={{ color: '#E2E8F0', fontSize: 12 }}>{c.customer || '—'}</span></td>
            <td style={tdSt}><span style={{ color: '#94A3B8', fontSize: 12 }}>{c.description || '—'}</span></td>
            <td style={tdSt}><span style={{ color: '#A5B4FC', fontSize: 12 }}>{BILLING_LABEL[c.billingType] || c.billingType}</span></td>
            <td style={{ ...tdSt, fontWeight: 700, color: '#E2E8F0' }}>{moeda(c.value)}</td>
            <td style={{ ...tdSt, color: '#94A3B8', fontSize: 12 }}>{fmtData(c.dueDate)}</td>
            <td style={tdSt}><Chip s={c.status} /></td>
            <td style={{ ...tdSt, display: 'flex', gap: 8, alignItems: 'center' }}>
              {c.billingType === 'PIX' && c.status === 'PENDING' && (
                <button onClick={() => verPix(c.id)} disabled={loadingAcao[c.id] === 'pix'} style={btnAcao('#818CF8')}>
                  {loadingAcao[c.id] === 'pix' ? '...' : 'QR Code'}
                </button>
              )}
              {c.billingType === 'BOLETO' && c.status === 'PENDING' && (
                <button onClick={() => verBoleto(c.id)} disabled={loadingAcao[c.id] === 'boleto'} style={btnAcao('#34D399')}>
                  {loadingAcao[c.id] === 'boleto' ? '...' : 'Linha Dig.'}
                </button>
              )}
              {c.invoiceUrl && <a href={c.invoiceUrl} target="_blank" rel="noreferrer" style={{ ...btnAcao('#60A5FA'), textDecoration: 'none', display: 'inline-block' }}>Fatura</a>}
              {c.status === 'PENDING' && (
                <button onClick={() => cancelar(c.id)} disabled={loadingAcao[c.id] === 'cancel'} style={btnAcao('#F87171')}>
                  {loadingAcao[c.id] === 'cancel' ? '...' : 'Cancelar'}
                </button>
              )}
            </td>
          </tr>
        ))}
        {!loading && !cobrancas.length && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#475569', padding: 40, fontSize: 13 }}>Nenhuma cobrança encontrada.</td></tr>}
      </Tabela>

      {/* Modal Nova Cobrança */}
      <Modal open={modalNova} onClose={() => setModalNova(false)} title="Nova Cobrança no Asaas">
        <FormNovaCobranca clientes={clientes.filter(c => c.asaas_customer_id)} onSalvar={criarCobranca} onFechar={() => setModalNova(false)} />
      </Modal>

      {/* Modal PIX QR Code */}
      <Modal open={!!modalPix} onClose={() => setModalPix(null)} title="PIX — QR Code">
        {modalPix && <ModalPix dados={modalPix} />}
      </Modal>

      {/* Modal Boleto */}
      <Modal open={!!modalBoleto} onClose={() => setModalBoleto(null)} title="Boleto — Linha Digitável">
        {modalBoleto && <ModalBoleto dados={modalBoleto} />}
      </Modal>
    </div>
  );
}

function ModalPix({ dados }) {
  const copiar = () => { navigator.clipboard.writeText(dados.payload || ''); toast.success('Código PIX copiado!'); };
  return (
    <div style={{ textAlign: 'center' }}>
      {dados.encodedImage && (
        <img src={`data:image/png;base64,${dados.encodedImage}`} alt="QR Code PIX"
          style={{ width: 220, height: 220, margin: '0 auto 16px', display: 'block', borderRadius: 8, background: '#fff', padding: 8 }} />
      )}
      {dados.payload && (
        <div>
          <p style={{ color: '#94A3B8', fontSize: 11, marginBottom: 6 }}>Copia e Cola PIX:</p>
          <div style={{ background: '#0B1220', borderRadius: 8, padding: 12, wordBreak: 'break-all', fontSize: 11, color: '#A5B4FC', marginBottom: 12 }}>
            {dados.payload}
          </div>
          <button onClick={copiar} style={btnPrimary}>Copiar código PIX</button>
        </div>
      )}
      {dados.expirationDate && (
        <p style={{ color: '#64748B', fontSize: 11, marginTop: 12 }}>Expira em {fmtData(dados.expirationDate)}</p>
      )}
    </div>
  );
}

function ModalBoleto({ dados }) {
  const copiar = () => { navigator.clipboard.writeText(dados.identificationField || ''); toast.success('Linha digitável copiada!'); };
  return (
    <div>
      <p style={{ color: '#94A3B8', fontSize: 12, marginBottom: 8 }}>Linha Digitável:</p>
      <div style={{ background: '#0B1220', borderRadius: 8, padding: 14, fontSize: 13, color: '#E2E8F0', fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: 12 }}>
        {dados.identificationField || '—'}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={copiar} style={btnPrimary}>Copiar linha digitável</button>
        {dados.bankSlipUrl && (
          <a href={dados.bankSlipUrl} target="_blank" rel="noreferrer"
            style={{ ...btnPrimary, background: '#1E3A5F', textDecoration: 'none', display: 'inline-block' }}>
            Abrir PDF
          </a>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ABA EXTRATO
// ═══════════════════════════════════════════════════════════════════════════════

function AbaExtrato() {
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
  const [filtros, setFiltros] = useState({ startDate: primeiroDia, finishDate: hoje.toISOString().split('T')[0] });
  const [extrato, setExtrato] = useState([]);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filtros);
      const { data } = await apiFetch(`/financeiro/asaas/extrato?${params}`);
      setExtrato(data || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const TIPO_LABEL = {
    PAYMENT: 'Pagamento recebido', PAYMENT_REFUND: 'Estorno emitido',
    TRANSFER: 'Transferência', INVOICE: 'Fatura', RECEIVED_IN_CASH: 'Dinheiro',
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        <label style={{ color: '#94A3B8', fontSize: 12 }}>De</label>
        <input type="date" value={filtros.startDate} onChange={e => setFiltros(p => ({ ...p, startDate: e.target.value }))} style={inputSt} />
        <label style={{ color: '#94A3B8', fontSize: 12 }}>até</label>
        <input type="date" value={filtros.finishDate} onChange={e => setFiltros(p => ({ ...p, finishDate: e.target.value }))} style={inputSt} />
      </div>

      <Tabela loading={loading} colunas={['Data', 'Tipo', 'Descrição', 'Valor', 'Saldo após']}>
        {extrato.map((e, i) => (
          <tr key={i}>
            <td style={{ ...tdSt, color: '#94A3B8', fontSize: 12 }}>{fmtData(e.date)}</td>
            <td style={tdSt}><span style={{ color: '#A5B4FC', fontSize: 12 }}>{TIPO_LABEL[e.type] || e.type}</span></td>
            <td style={{ ...tdSt, color: '#E2E8F0', fontSize: 12 }}>{e.description || '—'}</td>
            <td style={{ ...tdSt, fontWeight: 700, color: e.value >= 0 ? '#34D399' : '#F87171' }}>
              {e.value >= 0 ? '+' : ''}{moeda(e.value)}
            </td>
            <td style={{ ...tdSt, color: '#64748B', fontSize: 12 }}>{moeda(e.balance)}</td>
          </tr>
        ))}
        {!loading && !extrato.length && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#475569', padding: 40, fontSize: 13 }}>Nenhum lançamento no período.</td></tr>}
      </Tabela>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ABA TRANSFERÊNCIAS
// ═══════════════════════════════════════════════════════════════════════════════

function AbaTransferencias() {
  const [transferencias, setTransferencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNova, setModalNova] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/financeiro/asaas/transferencias');
      setTransferencias(data || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const criarTransferencia = async (form) => {
    try {
      await apiFetch('/financeiro/asaas/transferencias', { method: 'POST', body: JSON.stringify(form) });
      toast.success('Transferência criada!');
      setModalNova(false);
      carregar();
    } catch (e) { toast.error(e.message); }
  };

  const ST = { PENDING: 'pendente', DONE: 'pago', FAILED: 'vencido', CANCELLED: 'cancelado' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setModalNova(true)} style={btnPrimary}>+ Nova Transferência</button>
      </div>

      <Tabela loading={loading} colunas={['Data', 'Tipo', 'Valor', 'Descrição', 'Status']}>
        {transferencias.map(t => (
          <tr key={t.id}>
            <td style={{ ...tdSt, color: '#94A3B8', fontSize: 12 }}>{fmtData(t.transferDate || t.dateCreated)}</td>
            <td style={{ ...tdSt, color: '#A5B4FC', fontSize: 12 }}>{t.operationType === 'PIX' ? 'PIX' : t.operationType === 'TED' ? 'TED' : 'Conta Asaas'}</td>
            <td style={{ ...tdSt, fontWeight: 700, color: '#F87171' }}>-{moeda(t.value)}</td>
            <td style={{ ...tdSt, color: '#94A3B8', fontSize: 12 }}>{t.description || '—'}</td>
            <td style={tdSt}><Chip s={ST[t.status] || 'pendente'} /></td>
          </tr>
        ))}
        {!loading && !transferencias.length && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#475569', padding: 40, fontSize: 13 }}>Nenhuma transferência encontrada.</td></tr>}
      </Tabela>

      <Modal open={modalNova} onClose={() => setModalNova(false)} title="Nova Transferência Asaas">
        <FormNovaTransferencia onSalvar={criarTransferencia} onFechar={() => setModalNova(false)} />
      </Modal>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ABA RECORRENTES
// ═══════════════════════════════════════════════════════════════════════════════

const GRUPO_LABEL = { clientes: 'Receita de Clientes', legacy: 'Despesas Legacy', pessoal: 'Despesas Pessoais' };
const GRUPO_COR   = { clientes: '#34D399', legacy: '#F87171', pessoal: '#FB923C' };

function AbaRecorrentes() {
  const recorrentes = useAppStore(s => s.recorrentes);
  const addRecorrente    = useAppStore(s => s.addRecorrente);
  const updateRecorrente = useAppStore(s => s.updateRecorrente);
  const deleteRecorrente = useAppStore(s => s.deleteRecorrente);
  const toggleRecorrente = useAppStore(s => s.toggleRecorrente);

  const [modalNovo, setModalNovo] = useState(false);
  const [editando, setEditando] = useState(null);

  const grupos = useMemo(() => {
    const g = {};
    recorrentes.forEach(r => {
      if (!g[r.grupo]) g[r.grupo] = [];
      g[r.grupo].push(r);
    });
    return g;
  }, [recorrentes]);

  const totalReceitas  = useMemo(() => recorrentes.filter(r => r.tipo === 'receita' && r.ativo).reduce((s, r) => s + r.valor, 0), [recorrentes]);
  const totalDespesas  = useMemo(() => recorrentes.filter(r => r.tipo === 'despesa' && r.ativo).reduce((s, r) => s + r.valor, 0), [recorrentes]);
  const saldoMensal    = totalReceitas - totalDespesas;

  return (
    <div>
      {/* Resumo mensal */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Receita mensal', value: totalReceitas, cor: '#34D399' },
          { label: 'Despesa mensal', value: totalDespesas, cor: '#F87171' },
          { label: 'Saldo mensal',   value: saldoMensal,   cor: saldoMensal >= 0 ? '#34D399' : '#F87171' },
        ].map(c => (
          <div key={c.label} style={{ background: '#131D31', borderRadius: 12, padding: '16px 22px', flex: 1, minWidth: 160 }}>
            <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: c.cor, marginTop: 6 }}>{moeda(c.value)}</div>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 12 }}>
          <button onClick={() => setModalNovo(true)} style={btnPrimary}>+ Novo Recorrente</button>
        </div>
      </div>

      {/* Colunas por grupo */}
      <div className="resp-rec-cols" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {['clientes', 'legacy', 'pessoal'].map(grupo => {
          const itens = (grupos[grupo] || []).sort((a, b) => a.dia_vencimento - b.dia_vencimento);
          const total = itens.filter(i => i.ativo).reduce((s, i) => s + i.valor, 0);
          return (
            <div key={grupo} style={{ background: '#131D31', borderRadius: 14, overflow: 'hidden', border: '1px solid #2A3652' }}>
              {/* Cabeçalho do grupo */}
              <div style={{ background: '#0B1220', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `2px solid ${GRUPO_COR[grupo]}` }}>
                <span style={{ color: GRUPO_COR[grupo], fontWeight: 700, fontSize: 13 }}>{GRUPO_LABEL[grupo]}</span>
                <span style={{ color: GRUPO_COR[grupo], fontWeight: 700, fontSize: 14 }}>{moeda(total)}</span>
              </div>

              {/* Itens */}
              <div style={{ padding: '8px 0' }}>
                {itens.map(item => (
                  <div key={item.id} style={{
                    display: 'flex', alignItems: 'center', padding: '8px 14px', gap: 8,
                    opacity: item.ativo ? 1 : 0.4, borderBottom: '1px solid #1E293B',
                  }}>
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: '#0B1220', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#64748B' }}>{String(item.dia_vencimento).padStart(2, '0')}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#E2E8F0', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.descricao}</div>
                      <div style={{ color: GRUPO_COR[grupo], fontSize: 12, fontWeight: 700 }}>{moeda(item.valor)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => toggleRecorrente(item.id)} title={item.ativo ? 'Pausar' : 'Ativar'}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: item.ativo ? '#34D399' : '#475569', fontSize: 14 }}>
                        {item.ativo ? '●' : '○'}
                      </button>
                      <button onClick={() => setEditando(item)} title="Editar"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 12 }}>✎</button>
                      <button onClick={() => { if (window.confirm('Remover?')) deleteRecorrente(item.id); }} title="Remover"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14 }}>×</button>
                    </div>
                  </div>
                ))}
                {itens.length === 0 && (
                  <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', padding: 20 }}>Nenhum item</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={modalNovo || !!editando} onClose={() => { setModalNovo(false); setEditando(null); }} title={editando ? 'Editar Recorrente' : 'Novo Recorrente'}>
        <FormRecorrente
          inicial={editando}
          onSalvar={d => {
            if (editando) updateRecorrente(editando.id, d);
            else addRecorrente(d);
            setModalNovo(false); setEditando(null);
            toast.success(editando ? 'Atualizado!' : 'Recorrente adicionado!');
          }}
          onFechar={() => { setModalNovo(false); setEditando(null); }}
        />
      </Modal>
    </div>
  );
}

function FormRecorrente({ inicial, onSalvar, onFechar }) {
  const [form, setForm] = useState(inicial || { tipo: 'receita', grupo: 'clientes', descricao: '', valor: '', dia_vencimento: 5, categoria: 'mensalidade' });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar({ ...form, valor: parseFloat(form.valor), dia_vencimento: parseInt(form.dia_vencimento) }); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Campo label="Tipo">
          <select style={inputSt} value={form.tipo} onChange={e => s('tipo', e.target.value)}>
            <option value="receita">Receita</option>
            <option value="despesa">Despesa</option>
          </select>
        </Campo>
        <Campo label="Grupo">
          <select style={inputSt} value={form.grupo} onChange={e => s('grupo', e.target.value)}>
            <option value="clientes">Clientes</option>
            <option value="legacy">Legacy (agência)</option>
            <option value="pessoal">Pessoal</option>
          </select>
        </Campo>
      </div>
      <Campo label="Descrição *"><input style={inputSt} value={form.descricao} onChange={e => s('descricao', e.target.value)} required /></Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Campo label="Valor (R$) *"><input type="number" step="0.01" style={inputSt} value={form.valor} onChange={e => s('valor', e.target.value)} required /></Campo>
        <Campo label="Dia vencimento *"><input type="number" min="1" max="31" style={inputSt} value={form.dia_vencimento} onChange={e => s('dia_vencimento', e.target.value)} required /></Campo>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="submit" style={{ ...btnPrimary, flex: 1 }}>Salvar</button>
        <button type="button" onClick={onFechar} style={btnSecondary}>Cancelar</button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ABA DÍVIDAS
// ═══════════════════════════════════════════════════════════════════════════════

const CAT_CONFIG = {
  emocional: { label: 'Emocional',  cor: '#F87171', bg: '#450A0A', desc: 'Não pode deixar de pagar' },
  reais:     { label: 'Reais',      cor: '#FBBF24', bg: '#451A03', desc: 'Próximas a pagar' },
  bancaria:  { label: 'Bancária',   cor: '#60A5FA', bg: '#0C1A2E', desc: 'Pagarei um dia' },
};

function AbaDividas() {
  const dividas        = useAppStore(s => s.dividas);
  const addDivida      = useAppStore(s => s.addDivida);
  const updateDivida   = useAppStore(s => s.updateDivida);
  const deleteDivida   = useAppStore(s => s.deleteDivida);
  const amortizarDivida = useAppStore(s => s.amortizarDivida);

  const [modalNova, setModalNova] = useState(false);
  const [editando, setEditando]   = useState(null);
  const [amortizando, setAmortizando] = useState(null); // { id, valor }

  const totalGeral = dividas.reduce((s, d) => s + (d.valor_total - d.valor_amortizado), 0);

  return (
    <div>
      {/* Total geral */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
        <div style={{ background: '#131D31', borderRadius: 12, padding: '16px 22px' }}>
          <div style={{ fontSize: 11, color: '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>Total em dívidas</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#F87171', marginTop: 4 }}>{moeda(totalGeral)}</div>
        </div>
        {Object.entries(CAT_CONFIG).map(([cat, cfg]) => {
          const total = dividas.filter(d => d.categoria === cat).reduce((s, d) => s + (d.valor_total - d.valor_amortizado), 0);
          return (
            <div key={cat} style={{ background: cfg.bg, borderRadius: 12, padding: '16px 22px', border: `1px solid ${cfg.cor}33` }}>
              <div style={{ fontSize: 11, color: cfg.cor, fontWeight: 600, textTransform: 'uppercase' }}>{cfg.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: cfg.cor, marginTop: 4 }}>{moeda(total)}</div>
            </div>
          );
        })}
        <div style={{ flex: 1 }} />
        <button onClick={() => setModalNova(true)} style={btnPrimary}>+ Nova Dívida</button>
      </div>

      {/* Colunas */}
      <div className="resp-rec-cols" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {Object.entries(CAT_CONFIG).map(([cat, cfg]) => {
          const itens = dividas.filter(d => d.categoria === cat);
          const total = itens.reduce((s, d) => s + (d.valor_total - d.valor_amortizado), 0);
          return (
            <div key={cat} style={{ background: '#131D31', borderRadius: 14, overflow: 'hidden', border: `1px solid ${cfg.cor}44` }}>
              {/* Header */}
              <div style={{ background: cfg.bg, padding: '12px 16px', borderBottom: `2px solid ${cfg.cor}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ color: cfg.cor, fontWeight: 700, fontSize: 14 }}>{cfg.label}</span>
                  <span style={{ color: cfg.cor, fontWeight: 700, fontSize: 15 }}>{moeda(total)}</span>
                </div>
                <div style={{ color: cfg.cor + '99', fontSize: 11, marginTop: 2 }}>{cfg.desc}</div>
              </div>

              {/* Itens */}
              <div style={{ padding: '8px 0' }}>
                {itens.map(d => {
                  const restante = d.valor_total - d.valor_amortizado;
                  const pct = d.valor_total > 0 ? (d.valor_amortizado / d.valor_total) * 100 : 0;
                  return (
                    <div key={d.id} style={{ padding: '10px 14px', borderBottom: '1px solid #1E293B' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div>
                          <div style={{ color: '#E2E8F0', fontWeight: 600, fontSize: 13 }}>{d.credor}</div>
                          {d.observacoes && <div style={{ color: '#64748B', fontSize: 11 }}>{d.observacoes}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => setAmortizando({ id: d.id, credor: d.credor })} title="Registrar pagamento"
                            style={{ background: cfg.cor + '22', border: `1px solid ${cfg.cor}55`, color: cfg.cor, borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>
                            Pagar
                          </button>
                          <button onClick={() => setEditando(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: 12 }}>✎</button>
                          <button onClick={() => { if (window.confirm('Remover esta dívida?')) deleteDivida(d.id); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14 }}>×</button>
                        </div>
                      </div>

                      {/* Valores */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                        <span style={{ color: '#64748B' }}>Pago: <span style={{ color: '#34D399', fontWeight: 600 }}>{moeda(d.valor_amortizado)}</span></span>
                        <span style={{ color: '#64748B' }}>Restante: <span style={{ color: cfg.cor, fontWeight: 700 }}>{moeda(restante)}</span></span>
                      </div>

                      {/* Barra de progresso */}
                      <div style={{ background: '#0B1220', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: cfg.cor, borderRadius: 4, transition: 'width .4s' }} />
                      </div>
                      <div style={{ color: '#475569', fontSize: 10, marginTop: 3 }}>
                        {pct.toFixed(0)}% quitado · Total: {moeda(d.valor_total)}
                      </div>
                    </div>
                  );
                })}
                {itens.length === 0 && (
                  <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', padding: 24 }}>Nenhuma dívida</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal nova / editar dívida */}
      <Modal open={modalNova || !!editando} onClose={() => { setModalNova(false); setEditando(null); }} title={editando ? 'Editar Dívida' : 'Nova Dívida'}>
        <FormDivida
          inicial={editando}
          onSalvar={d => {
            if (editando) updateDivida(editando.id, d);
            else addDivida(d);
            setModalNova(false); setEditando(null);
            toast.success(editando ? 'Atualizado!' : 'Dívida adicionada!');
          }}
          onFechar={() => { setModalNova(false); setEditando(null); }}
        />
      </Modal>

      {/* Modal amortização */}
      <Modal open={!!amortizando} onClose={() => setAmortizando(null)} title={`Registrar pagamento — ${amortizando?.credor}`}>
        {amortizando && <FormAmortizacao
          onSalvar={v => { amortizarDivida(amortizando.id, v); toast.success('Pagamento registrado!'); setAmortizando(null); }}
          onFechar={() => setAmortizando(null)}
        />}
      </Modal>
    </div>
  );
}

function FormDivida({ inicial, onSalvar, onFechar }) {
  const [form, setForm] = useState(inicial || { credor: '', valor_total: '', valor_amortizado: 0, parcela_mensal: '', categoria: 'emocional', observacoes: '' });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar({ ...form, valor_total: parseFloat(form.valor_total), valor_amortizado: parseFloat(form.valor_amortizado || 0), parcela_mensal: parseFloat(form.parcela_mensal || 0) }); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Campo label="Credor *"><input style={inputSt} value={form.credor} onChange={e => s('credor', e.target.value)} required /></Campo>
      <Campo label="Categoria">
        <select style={inputSt} value={form.categoria} onChange={e => s('categoria', e.target.value)}>
          <option value="emocional">Emocional (prioridade máxima)</option>
          <option value="reais">Reais (próximas a pagar)</option>
          <option value="bancaria">Bancária (pagarei um dia)</option>
        </select>
      </Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Campo label="Valor total da dívida *"><input type="number" step="0.01" style={inputSt} value={form.valor_total} onChange={e => s('valor_total', e.target.value)} required /></Campo>
        <Campo label="Já amortizado (R$)"><input type="number" step="0.01" style={inputSt} value={form.valor_amortizado} onChange={e => s('valor_amortizado', e.target.value)} /></Campo>
      </div>
      <Campo label="Observações"><input style={inputSt} value={form.observacoes} onChange={e => s('observacoes', e.target.value)} placeholder="Ex: dívida antiga de 2022..." /></Campo>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="submit" style={{ ...btnPrimary, flex: 1 }}>Salvar</button>
        <button type="button" onClick={onFechar} style={btnSecondary}>Cancelar</button>
      </div>
    </form>
  );
}

function FormAmortizacao({ onSalvar, onFechar }) {
  const [valor, setValor] = useState('');
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(valor); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Campo label="Valor pago agora (R$) *">
        <input type="number" step="0.01" style={{ ...inputSt, fontSize: 18, fontWeight: 700 }} value={valor} onChange={e => setValor(e.target.value)} required autoFocus />
      </Campo>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={{ ...btnPrimary, flex: 1 }}>Registrar pagamento</button>
        <button type="button" onClick={onFechar} style={btnSecondary}>Cancelar</button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FORMULÁRIOS
// ═══════════════════════════════════════════════════════════════════════════════

function FormNovaTransacao({ clientes, onSalvar, onFechar }) {
  const [form, setForm] = useState({ tipo: 'receita', categoria: '', descricao: '', valor: '', data_vencimento: '', cliente_id: '', observacoes: '' });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Campo label="Tipo"><select style={inputSt} value={form.tipo} onChange={e => s('tipo', e.target.value)}><option value="receita">Receita</option><option value="despesa">Despesa</option></select></Campo>
        <Campo label="Categoria">
          <select style={inputSt} value={form.categoria} onChange={e => s('categoria', e.target.value)}>
            <option value="">Selecione...</option>
            {['mensalidade','avulso','ferramentas','pessoal','infraestrutura','marketing','outro'].map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
          </select>
        </Campo>
      </div>
      <Campo label="Descrição *"><input style={inputSt} value={form.descricao} onChange={e => s('descricao', e.target.value)} required /></Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Campo label="Valor (R$) *"><input type="number" step="0.01" style={inputSt} value={form.valor} onChange={e => s('valor', e.target.value)} required /></Campo>
        <Campo label="Vencimento *"><input type="date" style={inputSt} value={form.data_vencimento} onChange={e => s('data_vencimento', e.target.value)} required /></Campo>
      </div>
      <Campo label="Cliente (opcional)">
        <select style={inputSt} value={form.cliente_id} onChange={e => s('cliente_id', e.target.value)}>
          <option value="">Sem cliente</option>
          {clientes.map(c => <option key={c.id} value={c.id}>{c.razao_social}</option>)}
        </select>
      </Campo>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="submit" style={{ ...btnPrimary, flex: 1 }}>Salvar</button>
        <button type="button" onClick={onFechar} style={btnSecondary}>Cancelar</button>
      </div>
    </form>
  );
}

function FormNovaCobranca({ clientes, onSalvar, onFechar }) {
  const [form, setForm] = useState({ asaas_customer_id: '', billingType: 'PIX', value: '', dueDate: '', description: '' });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar({ ...form, value: parseFloat(form.value) }); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {!clientes.length && (
        <div style={{ background: '#1E3A5F', borderRadius: 8, padding: 12, color: '#93C5FD', fontSize: 12 }}>
          ⚠ Nenhum cliente sincronizado com o Asaas. Sincronize os clientes na aba Clientes antes de criar cobranças.
        </div>
      )}
      <Campo label="Cliente Asaas *">
        <select style={inputSt} value={form.asaas_customer_id} onChange={e => s('asaas_customer_id', e.target.value)} required>
          <option value="">Selecione o cliente...</option>
          {clientes.map(c => <option key={c.id} value={c.asaas_customer_id}>{c.razao_social} ({c.asaas_customer_id})</option>)}
        </select>
      </Campo>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Campo label="Forma de pagamento">
          <select style={inputSt} value={form.billingType} onChange={e => s('billingType', e.target.value)}>
            <option value="PIX">PIX</option>
            <option value="BOLETO">Boleto</option>
            <option value="CREDIT_CARD">Cartão de crédito</option>
            <option value="UNDEFINED">Qualquer</option>
          </select>
        </Campo>
        <Campo label="Valor (R$) *"><input type="number" step="0.01" style={inputSt} value={form.value} onChange={e => s('value', e.target.value)} required /></Campo>
      </div>
      <Campo label="Vencimento *"><input type="date" style={inputSt} value={form.dueDate} onChange={e => s('dueDate', e.target.value)} required /></Campo>
      <Campo label="Descrição"><input style={inputSt} value={form.description} onChange={e => s('description', e.target.value)} /></Campo>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="submit" style={{ ...btnPrimary, flex: 1 }} disabled={!clientes.length}>Criar no Asaas</button>
        <button type="button" onClick={onFechar} style={btnSecondary}>Cancelar</button>
      </div>
    </form>
  );
}

function FormNovaTransferencia({ onSalvar, onFechar }) {
  const [form, setForm] = useState({ operationType: 'PIX', pixAddressKey: '', pixAddressKeyType: 'CPF', value: '', description: '', scheduleDate: '' });
  const s = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar({ ...form, value: parseFloat(form.value) }); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Campo label="Tipo">
        <select style={inputSt} value={form.operationType} onChange={e => s('operationType', e.target.value)}>
          <option value="PIX">PIX</option>
          <option value="TED">TED</option>
        </select>
      </Campo>
      {form.operationType === 'PIX' && <>
        <Campo label="Tipo da chave PIX">
          <select style={inputSt} value={form.pixAddressKeyType} onChange={e => s('pixAddressKeyType', e.target.value)}>
            <option value="CPF">CPF</option><option value="CNPJ">CNPJ</option>
            <option value="EMAIL">E-mail</option><option value="PHONE">Telefone</option>
            <option value="EVP">Chave aleatória</option>
          </select>
        </Campo>
        <Campo label="Chave PIX *"><input style={inputSt} value={form.pixAddressKey} onChange={e => s('pixAddressKey', e.target.value)} required /></Campo>
      </>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Campo label="Valor (R$) *"><input type="number" step="0.01" style={inputSt} value={form.value} onChange={e => s('value', e.target.value)} required /></Campo>
        <Campo label="Agendar para"><input type="date" style={inputSt} value={form.scheduleDate} onChange={e => s('scheduleDate', e.target.value)} /></Campo>
      </div>
      <Campo label="Descrição"><input style={inputSt} value={form.description} onChange={e => s('description', e.target.value)} /></Campo>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="submit" style={{ ...btnPrimary, flex: 1 }}>Transferir</button>
        <button type="button" onClick={onFechar} style={btnSecondary}>Cancelar</button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════════

function Tabela({ colunas, children, loading }) {
  return (
    <div style={{ background: '#131D31', borderRadius: 14, overflow: 'hidden', border: '1px solid #2A3652' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#0B1220' }}>
              {colunas.map(c => (
                <th key={c} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.8, whiteSpace: 'nowrap' }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colunas.length} style={{ textAlign: 'center', color: '#475569', padding: 40, fontSize: 13 }}>Carregando...</td></tr>
            ) : children}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  );
}

// ─── ESTILOS INLINE ───────────────────────────────────────────────────────────

const inputSt = {
  background: '#19243A', border: '1px solid #2A3652', borderRadius: 8,
  color: '#E2E8F0', padding: '8px 12px', fontSize: 13, width: '100%', outline: 'none',
};

const tdSt = { padding: '11px 14px', borderBottom: '1px solid #1E293B', verticalAlign: 'middle' };

const btnPrimary = {
  background: '#4F46E5', color: '#fff', border: 'none', borderRadius: 8,
  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnSecondary = {
  background: '#1E293B', color: '#94A3B8', border: '1px solid #2A3652', borderRadius: 8,
  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const btnAcao = (cor) => ({
  background: 'transparent', border: `1px solid ${cor}`, color: cor,
  borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  whiteSpace: 'nowrap',
});
